import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Loader2, Save, Trash2, Plus, X,
  HardHat, Package, Wrench, Sparkles, AlertTriangle, Check, Info, Archive,
  ArrowUpFromLine, ArrowDownToLine, FileText,
} from "lucide-react";
import Bookmark from "lucide-react/dist/esm/icons/bookmark";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { computeRecipeTotals } from "../lib/recipe";
import {
  Card, Field, Input, NumberInput, Textarea, Select, ToggleGroup,
  MultiSelect, PrimaryButton, QuietButton, GhostButton, StickyHeader, Modal
} from "../components/UI";
import {
  LabourSection, MaterialsSection, SimpleCostSection, RecipeSummary,
  UNIT_OPTIONS, blockEnterSubmit
} from "../components/RecipeBuilder";
import { PriceHistoryPanel } from "../components/PriceHistoryPanel";

// Tiny classname joiner. Falsy classes are filtered so we can pass
// conditionals inline: cls("base", active && "active"). Local to this
// page to avoid an extra import.
const cls = (...xs) => xs.filter(Boolean).join(" ");

const newId = () => uuidv4();
const f = (v) => v == null || v === "" ? 0 : Number(v) || 0;

/* ---------------- Save validation modal ---------------- */
function ValidationModal({ open, onClose, checks, onConfirm, canPromote = false }) {
  const blocking = checks.filter((c) => c.severity === "required" && !c.pass);
  const canProceed = blocking.length === 0;
  // LT-32 G2 tweak — optional "promote to Live" on save (draft + valid recipe only).
  const [promote, setPromote] = React.useState(false);
  React.useEffect(() => { if (open) setPromote(false); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={canProceed ? "Save library item" : "Cannot save yet"}
      testid="library-save-modal"
      footer={
        <>
          <QuietButton onClick={onClose} data-testid="modal-cancel">
            {canProceed ? "Go back and edit" : "Go back and fix"}
          </QuietButton>
          {canProceed && (
            <PrimaryButton onClick={() => onConfirm(promote)} icon={Save} data-testid="modal-confirm-save">
              {promote
                ? "Save & promote to Live"
                : blocking.length === 0 && checks.some((c) => !c.pass)
                  ? "Save anyway"
                  : "Save"}
            </PrimaryButton>
          )}
        </>
      }
    >
      <p className="text-ink-3 mb-4" style={{ fontSize: 12.5 }}>
        {canProceed
          ? "All required fields are present. Review any warnings, then save."
          : "Some required fields are missing. Fix them to enable save."}
      </p>
      <ul className="space-y-2.5">
        {checks.map((c, i) => {
          const ok = !!c.pass;
          let bg, fg, Icon;
          if (c.severity === "required") {
            if (ok) { bg = "#E8EFE9"; fg = "#2F4F3E"; Icon = Check; }
            else    { bg = "#F4E6E4"; fg = "#9B3A37"; Icon = AlertTriangle; }
          } else if (c.severity === "warning") {
            if (ok) { bg = "#E8EFE9"; fg = "#2F4F3E"; Icon = Check; }
            else    { bg = "#FEF3E2"; fg = "#7A4C0A"; Icon = AlertTriangle; }
          } else { // info
            if (ok) { bg = "#E8EFE9"; fg = "#2F4F3E"; Icon = Check; }
            else    { bg = "#EEF2F8"; fg = "#3D5478"; Icon = Info; }
          }
          return (
            <li
              key={i}
              data-testid={`validation-check-${i}`}
              className="flex items-start gap-2.5 px-3 py-2 rounded-md"
              style={{ background: bg, color: fg, fontSize: 12.5 }}
            >
              <Icon size={13} style={{ flex: "0 0 auto", marginTop: 2 }} />
              <span style={{ flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{c.label}</span>
                {c.detail && <span className="block opacity-80 mt-0.5" style={{ fontSize: 11.5 }}>{c.detail}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {canProceed && canPromote && (
        <label
          className="mt-4 flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-accent bg-accent-soft cursor-pointer"
          data-testid="promote-on-save-label"
        >
          <input
            type="checkbox"
            checked={promote}
            onChange={(e) => setPromote(e.target.checked)}
            data-testid="promote-on-save-checkbox"
            className="accent-accent mt-0.5 cursor-pointer"
          />
          <span className="text-accent-deep" style={{ fontSize: 12.5 }}>
            <span style={{ fontWeight: 600 }}>Promote recipe to Live after saving</span>
            <span className="block opacity-80 mt-0.5" style={{ fontSize: 11.5 }}>
              Saves the built-up recipe, then makes this item Live and usable in tenders. Leave
              unticked to keep it as a Draft.
            </span>
          </span>
        </label>
      )}
    </Modal>
  );
}

/* ---------------- main ---------------- */
// LT-32 G2 — stable-ish id for a pre-filled (unsaved) suggestion line.
const pfId = () => `pf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export default function LibraryEdit() {
  const { id } = useParams();
  // LT-36 — `?build=1` (from the tender's "Build recipe →" link) opens the
  // Materials section straight into build-out mode.
  const [searchParams] = useSearchParams();
  const buildOutRequested = searchParams.get("build") === "1";
  const navigate = useNavigate();
  const editing = !!id;
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  // LT-21a §5 — tenders still carrying lines from this item (read-only).
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    if (!editing) return;
    api.get(`/library/${id}/references`).then((r) => setRefs(r.data)).catch(() => setRefs(null));
  }, [editing, id]);

  const [phases, setPhases] = useState([]);
  const [labourRates, setLabourRates] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [form, setForm] = useState({
    sku: "",
    description: "",
    unit: "",
    phase_tags: [],         // unified Phase / Category tags (legacy category_tags merged in)
    trade_code: "",
    trade_name: "",
    trade_manual_override: false,  // false = auto-recalculate from recipe; true = user-locked
    item_type: "priced",
    notes: "",
    default_note: "",
    recipe_labour: [],
    recipe_materials: [],
    recipe_plant: [],
    recipe_subby: [],
    // LT-Estimate-UX-01 Section 4 — labour mode: "rate_based" (default) or
    // "estimated_each_use". See LibraryItem.labour_mode docstring.
    labour_mode: "rate_based",
    // LT-36 — Flat Material Rate (one £/unit figure, nothing itemised).
    flat_material_enabled: false,
    flat_material_rate: null,
    flat_material_note: "",
  });
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const flat = { enabled: !!form.flat_material_enabled, rate: form.flat_material_rate, note: form.flat_material_note };
  const setFlat = (v) => setForm((s) => ({ ...s, flat_material_enabled: !!v.enabled, flat_material_rate: v.rate ?? null, flat_material_note: v.note ?? "" }));

  // Validation modal
  const [modalOpen, setModalOpen] = useState(false);
  // LT-32 G2 — when a draft with an empty recipe carries imported reference
  // figures, we pre-fill the labour hours + plant rate as an EDITABLE
  // suggestion. This note drives the "suggested from import" banner. It is
  // purely informational — nothing is written until the user clicks Save.
  const [prefillNote, setPrefillNote] = useState(null);  // { hours, plant } | null

  // -- Load --
  useEffect(() => {
    Promise.all([
      api.get("/settings/master-phases").then((r) => setPhases((r.data || []).map((p) => p.name))).catch(() => {}),
      api.get("/labour-rates").then((r) => setLabourRates(r.data || [])).catch(() => {}),
      api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => {})
    ]);
    if (!editing) { setLoading(false); return; }
    // LT-27: allow loading a DRAFT item on this page (backend `GET /library/{id}`
    // returns any status). Pass status=all defensively in case any listing
    // path pre-filters.
    api.get(`/library/${id}`).then((r) => {
      setDoc(r.data);
      // Merge legacy category_tags into the unified phase_tags multi-select
      const merged = Array.from(new Set([
        ...(r.data.phase_tags || []),
        ...(r.data.category_tags || [])
      ]));
      // LT-32 G2 — pre-fill suggestion. Only for a DRAFT whose recipe is
      // entirely empty AND which carries imported reference figures. We
      // suggest a labour line (hours only, no trade/rate) and a plant line.
      // The material line is intentionally NOT pre-filled. These are unsaved
      // starting points — the trade auto-derive + save flow treat them like
      // any other user-entered line.
      const recipeEmpty =
        !(r.data.recipe_labour || []).length
        && !(r.data.recipe_materials || []).length
        && !(r.data.recipe_plant || []).length;
      const hoursMed = r.data.ref_hours_median;
      const plantMed = r.data.ref_plant_rate;
      const canPrefill =
        r.data.status === "draft" && recipeEmpty
        && (hoursMed != null || plantMed != null);
      const prefillLabour = canPrefill && hoursMed != null
        ? [{ id: pfId(), trade_name: "", hours_per_unit: hoursMed, hourly_rate: null, labour_rate_id: null }]
        : (r.data.recipe_labour || []);
      const prefillPlant = canPrefill && plantMed != null
        ? [{ id: pfId(), description: "Imported plant allowance — review", cost_per_unit: plantMed }]
        : (r.data.recipe_plant || []);
      setPrefillNote(canPrefill ? { hours: hoursMed, plant: plantMed } : null);
      setForm({
        sku: r.data.sku || "",
        description: r.data.description || "",
        unit: r.data.unit || "",
        phase_tags: merged,
        trade_code: r.data.trade_code || "",
        trade_name: r.data.trade_name || "",
        // Existing items default to auto-mode on first edit unless they were
        // previously saved with manual override flagged. Legacy items (created
        // before this field existed) load as auto-mode (false).
        trade_manual_override: r.data.trade_manual_override === true,
        item_type: r.data.item_type || "priced",
        notes: r.data.notes || "",
        default_note: r.data.default_note || "",
        recipe_labour: prefillLabour,
        // LT-36 — `?build=1` on a flat item with nothing itemised yet seeds one
        // empty material line so build-out starts with a row to fill in.
        recipe_materials: (buildOutRequested && r.data.flat_material_enabled && !(r.data.recipe_materials || []).length)
          ? [{ id: pfId(), description: "", qty_per_unit: null, unit: "", rate: null, material_id: null, wastage_pct: null, coverage_basis: "purchase_unit" }]
          : (r.data.recipe_materials || []),
        recipe_plant: prefillPlant,
        recipe_subby: r.data.recipe_subby || [],
        // LT-Estimate-UX-01 Section 4 — labour mode. Legacy items without
        // the field load as "rate_based" (safe default: they had a real rate).
        labour_mode: r.data.labour_mode === "estimated_each_use" ? "estimated_each_use" : "rate_based",
        flat_material_enabled: !!r.data.flat_material_enabled,
        flat_material_rate: r.data.flat_material_rate ?? null,
        flat_material_note: r.data.flat_material_note || "",
      });
    }).catch(() => toast.error("Library item not found")).finally(() => setLoading(false));
  }, [id, editing, buildOutRequested]);

  // -- Auto-default Trade based on highest-hours labour line --
  // Runs whenever recipe_labour changes OR when the user resets to auto-mode.
  // Skipped while trade_manual_override is true (user has explicitly chosen a trade).
  useEffect(() => {
    if (form.trade_manual_override) return;
    if (!form.recipe_labour || form.recipe_labour.length === 0) {
      // Recipe has no labour: clear auto-derived trade
      if (form.trade_name || form.trade_code) {
        setForm((s) => ({ ...s, trade_name: "", trade_code: "" }));
      }
      return;
    }
    // Pick the line with highest total hours; first wins on tie (preserves order).
    let bestIdx = 0;
    let bestHours = f(form.recipe_labour[0].hours_per_unit);
    for (let i = 1; i < form.recipe_labour.length; i += 1) {
      const h = f(form.recipe_labour[i].hours_per_unit);
      if (h > bestHours) { bestHours = h; bestIdx = i; }
    }
    const winner = form.recipe_labour[bestIdx];
    const lr = labourRates.find((x) => x.id === winner.labour_rate_id);
    const tName = lr?.trade_name || winner.trade_name || "";
    const tCode = lr?.trade_code || "";
    if (tName !== form.trade_name || tCode !== form.trade_code) {
      setForm((s) => ({ ...s, trade_name: tName, trade_code: tCode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.recipe_labour, labourRates, form.trade_manual_override]);

  // Reset Trade field back to auto-mode and immediately recalculate.
  const resetTradeToAuto = () => {
    setTradeOverrideOpen(false);
    setForm((s) => ({ ...s, trade_manual_override: false }));
    // The useEffect above will fire on the trade_manual_override change and
    // populate trade_name / trade_code from the current recipe.
  };
  // LT-38 — in Auto mode the picker is hidden behind "Override manually".
  const [tradeOverrideOpen, setTradeOverrideOpen] = useState(false);
  const autoTradeHours = useMemo(() => {
    if (form.trade_manual_override || !form.trade_name) return null;
    const h = (form.recipe_labour || [])
      .filter((l) => (labourRates.find((x) => x.id === l.labour_rate_id)?.trade_name || l.trade_name) === form.trade_name)
      .reduce((n, l) => n + f(l.hours_per_unit), 0);
    return h > 0 ? Math.round(h * 100) / 100 : null;
  }, [form.recipe_labour, form.trade_name, form.trade_manual_override, labourRates]);

  const totals = useMemo(() => computeRecipeTotals(form), [form]);

  // -- Build validation checks --
  const checks = useMemo(() => {
    const tagsCount = (form.phase_tags || []).length;
    const labourEmpty = (form.recipe_labour || []).length === 0;
    const flatOn = !!form.flat_material_enabled;
    const materialsEmpty = flatOn ? !(f(form.flat_material_rate) > 0) : (form.recipe_materials || []).length === 0;
    const plantEmpty = (form.recipe_plant || []).length === 0;
    return [
      {
        label: "Description present",
        severity: "required",
        pass: !!form.description?.trim(),
        detail: form.description?.trim() ? null : "Add a clear description so the item is searchable.",
      },
      {
        label: "Unit selected",
        severity: "required",
        pass: !!form.unit,
        detail: form.unit ? null : "Pick a recipe yield unit (m, m², m³, item, nr, …).",
      },
      {
        label: "Trade selected",
        severity: "required",
        pass: !!form.trade_name,
        detail: form.trade_name ? null : "Pick a trade — required so the item surfaces in the right tender contexts.",
      },
      {
        label: "At least one Phase / Category tag",
        severity: "required",
        pass: tagsCount > 0,
        detail: tagsCount > 0 ? `${tagsCount} tag${tagsCount === 1 ? "" : "s"} added.` : "Tag the item against at least one phase or category.",
      },
      {
        label: "Labour section",
        severity: "warning",
        pass: !labourEmpty,
        detail: labourEmpty ? "No labour entries — most items include some labour. Confirm this is intentional." : null,
      },
      {
        label: flatOn ? "Materials — flat rate" : "Materials section",
        severity: "warning",
        pass: !materialsEmpty,
        detail: materialsEmpty
          ? (flatOn ? "Flat Material Rate is on but no £/unit figure entered — confirm this is intentional." : "No materials entries — confirm this is intentional.")
          : (flatOn ? "Priced as a Flat Material Rate — nothing itemised; build the recipe later from the 'Flat material' filter." : null),
      },
      {
        label: "Plant section",
        severity: "info",
        pass: !plantEmpty,
        detail: plantEmpty ? "No plant entries — most items don't need any. This is informational only." : null,
      },
    ];
  }, [form]);

  // -- Save flow --
  const triggerSave = () => {
    setModalOpen(true);
  };

  const performSave = async (promote = false) => {
    setSaving(true);
    try {
      const payload = {
        // SKU is auto-generated server-side and ignored if sent.
        description: form.description.trim(),
        unit: form.unit,
        phase_tags: form.phase_tags,
        category_tags: [], // legacy field — clear it; unified into phase_tags
        trade_code: form.trade_code || null,
        trade_name: form.trade_name || null,
        trade_manual_override: !!form.trade_manual_override,
        item_type: form.item_type,
        notes: form.notes,
        default_note: form.default_note || "",
        recipe_labour: form.recipe_labour.map((e) => ({
          ...e,
          hours_per_unit: f(e.hours_per_unit),
          hourly_rate: f(e.hourly_rate),
        })),
        recipe_materials: form.recipe_materials.map((e) => ({
          ...e,
          qty_per_unit: f(e.qty_per_unit),
          rate: f(e.rate),
        })),
        recipe_plant: form.recipe_plant.map((e) => ({
          ...e,
          cost_per_unit: f(e.cost_per_unit),
        })),
        recipe_subby: form.recipe_subby.map((e) => ({
          ...e,
          cost_per_unit: f(e.cost_per_unit),
        })),
        // LT-Estimate-UX-01 Section 4 — persist labour mode.
        labour_mode: form.labour_mode || "rate_based",
        // LT-36 — Flat Material Rate
        flat_material_enabled: !!form.flat_material_enabled,
        flat_material_rate: form.flat_material_enabled ? f(form.flat_material_rate) : (form.flat_material_rate != null ? f(form.flat_material_rate) : null),
        flat_material_note: form.flat_material_note || null,
        // Flat-rate fallback retained for legacy consumers
        current_rate: totals.has_recipe ? totals.current_rate : 0,
      };
      let res;
      if (editing) {
        res = await api.patch(`/library/${id}`, payload);
      } else {
        res = await api.post("/library", payload);
      }
      setModalOpen(false);

      // LT-32 G2 tweak — optional promote-to-Live on save. Runs AFTER the
      // recipe is persisted (the promote endpoint reads the saved recipe).
      if (promote) {
        const targetId = editing ? id : res.data.id;
        try {
          await api.post(`/library/${targetId}/promote`, { mode: "recipe" });
          toast.success("Saved and promoted to Live");
        } catch (e) {
          // Save succeeded; promotion failed — surface it and stay on the item.
          toast.error(e?.response?.data?.detail || "Saved, but promotion failed");
          setDoc(res.data);
          setForm((s) => ({ ...s, sku: res.data.sku || s.sku }));
          return;
        }
      } else {
        toast.success(editing ? "Library item saved" : `Created ${res.data.sku}`);
      }

      // LT-32 G2 tweak — after saving an EXISTING item (the draft-review
      // workflow), return to the Draft area of the Master Library rather than
      // staying on the item. New items still open so the user can keep editing.
      if (editing) {
        navigate("/library?status=draft");
      } else {
        navigate(`/library/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // LT-21a §5 — say what the archive leaves behind before doing it. Lines on
  // those tenders keep their snapshot; nothing is re-priced or removed.
  const archive = async () => {
    if (!editing) return;
    let refs = null;
    try { refs = (await api.get(`/library/${id}/references`)).data; } catch { refs = null; }
    const c = refs?.counts;
    const usage = c && c.tenders > 0
      ? `\n\nThis item is used on ${c.lines} line(s) across ${c.tenders} tender(s): ` +
        `${c.draft} live, ${c.issued} issued, ${c.archived} archived, ${c.trashed} in trash.` +
        `\nThose lines keep the rate they were priced at and will show an "archived item" marker.`
      : (c ? "\n\nNo tender lines reference this item." : "");
    if (!window.confirm(`Archive this library item? It will be hidden from the list.${usage}`)) return;
    try {
      const r = await api.delete(`/library/${id}`);
      const rc = r.data?.references?.counts;
      toast.success(rc && rc.tenders > 0 ? `Archived — still referenced by ${rc.tenders} tender(s)` : "Archived");
      navigate("/library");
    }
    catch { toast.error("Archive failed"); }
  };

  // LT-27 — Reload the current doc after promote/demote so header CTA + status
  // pill update without a full page reload.
  const reloadDoc = async () => {
    if (!editing) return;
    try {
      const r = await api.get(`/library/${id}`);
      setDoc(r.data);
    } catch { /* non-fatal */ }
  };

  // LT-34 — Review flag controls. These hit the DEDICATED endpoints only,
  // never the PATCH/save path: flagging is housekeeping, not an edit, so
  // saving other changes never touches the flag and flagging never counts as
  // an edit. `flagOpen` toggles the inline reason field so the natural action
  // at the point of flagging is to type a reason (optional, not blocking).
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagBusy, setFlagBusy] = useState(false);

  const submitFlag = async (overwrite) => {
    setFlagBusy(true);
    try {
      const r = await api.post(`/library/${id}/review-flag`, {
        reason: flagReason.trim() || null,
        overwrite: !!overwrite,
      });
      if (r.data?.already_flagged) {
        toast.warning("Already flagged — reopen to edit the reason.");
      } else {
        toast.success(r.data?.updated ? "Review reason updated" : "Flagged for review");
      }
      setFlagOpen(false);
      await reloadDoc();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not flag item");
    } finally {
      setFlagBusy(false);
    }
  };

  const clearFlag = async () => {
    setFlagBusy(true);
    try {
      await api.post(`/library/${id}/review-clear`);
      toast.success("Review flag cleared");
      setFlagOpen(false);
      await reloadDoc();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear flag");
    } finally {
      setFlagBusy(false);
    }
  };

  const openFlagEditor = () => {
    setFlagReason(doc?.review_reason || "");
    setFlagOpen(true);
  };

  const demoteToDraft = async () => {
    if (!editing || !window.confirm(
      "Demote this item to Draft?\n\nIt will be hidden from tender library pickers until promoted again."
    )) return;
    try {
      await api.post(`/library/${id}/demote`);
      toast.success("Demoted to Draft");
      await reloadDoc();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demote failed");
    }
  };

  // LT-27 close-out — hard-delete a draft. Distinct from `archive` above;
  // never available on live items (which use Archive instead).
  const deleteDraft = async () => {
    if (!editing) return;
    if (!window.confirm(
      "Delete this draft permanently?\n\nHard delete — the record cannot be recovered."
    )) return;
    try {
      await api.delete(`/library/${id}/draft`);
      toast.success("Draft deleted");
      navigate("/library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="app-container py-12 flex items-center justify-center">
        <Loader2 className="animate-spin text-ink-3" size={20} />
      </div>
    );
  }

  // Trade dropdown options (each labour rate is one option). No raw text entry.
  const tradeOptions = labourRates
    .filter((lr) => !lr.is_archived)
    .map((lr) => ({
      value: lr.id,
      label: lr.trade_code ? `${lr.trade_name} (${lr.trade_code})` : lr.trade_name,
      rate: lr,
    }));
  const selectedTradeRate = labourRates.find((lr) => lr.trade_name === form.trade_name);
  const tradeValue = selectedTradeRate?.id || "";

  return (
    <div className="app-container py-6" data-testid="library-edit-page">
      <StickyHeader
        breadcrumb={
          <>
            <button onClick={() => navigate("/library")} className="hover:text-ink-2" data-testid="back-to-library">Library</button>
            <ChevronRight size={12} />
            <span className="text-ink-2">{editing ? "Edit" : "New"}</span>
          </>
        }
        title={editing
          ? `Edit: ${doc?.description?.slice(0, 60) || "Library item"}`
          : "New library item"}
        actions={
          <>
            {editing && doc?.status === "draft" && (
              <PromoteWithRecipeButton doc={doc} totals={totals} onDone={reloadDoc} />
            )}
            {editing && doc?.status === "live" && (
              <QuietButton
                onClick={demoteToDraft}
                icon={ArrowDownToLine}
                data-testid="demote-to-draft-top"
              >
                Demote to Draft
              </QuietButton>
            )}
            {/* LT-27 close-out — Archive is for LIVE items only. Drafts get a
                Delete Draft action instead. Mutually exclusive by status. */}
            {editing && doc?.status === "live" && (
              <>
                {refs?.counts && (
                  <span
                    className="text-ink-3 inline-flex items-center"
                    style={{ fontSize: 11.5 }}
                    title={refs.counts.tenders > 0
                      ? `${refs.counts.lines} line(s) on ${refs.counts.tenders} tender(s): ${refs.counts.draft} live, ${refs.counts.issued} issued, ${refs.counts.archived} archived, ${refs.counts.trashed} in trash. Archiving keeps every one of those lines at the rate it was priced at.`
                      : "No tender lines reference this item."}
                    data-testid="library-item-references"
                  >
                    Used on <span className="font-mono text-ink mx-1" data-testid="library-item-references-count">{refs.counts.tenders}</span> tender(s)
                  </span>
                )}
                <QuietButton onClick={archive} icon={Archive} data-testid="archive-item-top">
                  Archive
                </QuietButton>
              </>
            )}
            {editing && doc?.status === "draft" && (
              <QuietButton
                onClick={deleteDraft}
                icon={Trash2}
                data-testid="delete-draft-top"
              >
                Delete draft
              </QuietButton>
            )}
            <PrimaryButton
              onClick={triggerSave}
              disabled={saving}
              icon={saving ? Loader2 : Save}
              data-testid="save-item-top"
            >
              {editing ? "Save changes" : "Save & create"}
            </PrimaryButton>
          </>
        }
      />

      {/* Title block (non-sticky) */}
      <div className="mt-7 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-ink" style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {editing ? doc?.description || "Library item" : "New library item"}
          </h1>
          {editing && doc && (
            <StatusChip status={doc.status} isBuiltUp={doc.is_built_up !== false} />
          )}
        </div>
        <p className="text-ink-3 mt-1" style={{ fontSize: 13 }}>
          Reusable priced item, provisional sum or prelim with a per-unit recipe.
          {editing && doc?.sku && (
            <span className="ml-2 font-mono tnum text-ink-4">· {doc.sku}</span>
          )}
        </p>
      </div>

      {/* LT-34 — Review flag card. Uses the dedicated review endpoints only;
          independent of the Save/PATCH flow. Violet/purple treatment with a
          Bookmark (not a warning triangle) — flagged items stay fully usable. */}
      {editing && doc && (
        <div
          className="mb-6 rounded-lg border p-4"
          data-testid="library-review-card"
          style={
            doc.needs_review
              ? { borderColor: "#c4b5fd", background: "#f5f3ff" }
              : { borderColor: "var(--line)", background: "var(--surface)" }
          }
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2.5">
              <Bookmark
                size={16}
                className={doc.needs_review ? "text-violet-600 mt-0.5" : "text-ink-4 mt-0.5"}
              />
              <div>
                {doc.needs_review ? (
                  <>
                    <div className="text-violet-800 font-medium" style={{ fontSize: 13 }}>
                      Flagged for review
                    </div>
                    {doc.review_reason ? (
                      <div className="text-ink-2 mt-0.5" style={{ fontSize: 12.5 }} data-testid="review-reason-text">
                        “{doc.review_reason}”
                      </div>
                    ) : (
                      <div className="text-ink-4 mt-0.5 italic" style={{ fontSize: 12.5 }} data-testid="review-reason-text">
                        No reason given
                      </div>
                    )}
                    <div className="text-ink-4 mt-1" style={{ fontSize: 11.5 }} data-testid="review-attribution">
                      {doc.review_flagged_by_name ? `${doc.review_flagged_by_name}` : "Someone"} flagged this
                      {doc.review_flagged_at ? ` on ${ukDate(doc.review_flagged_at)}` : ""}
                    </div>
                  </>
                ) : (
                  <div className="text-ink-3" style={{ fontSize: 12.5 }}>
                    Not flagged. Raise a review flag if this item needs a pricing or content check.
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {doc.needs_review ? (
                <>
                  <QuietButton onClick={openFlagEditor} disabled={flagBusy} data-testid="review-edit-btn">
                    Edit reason
                  </QuietButton>
                  <QuietButton onClick={clearFlag} disabled={flagBusy} data-testid="review-clear-btn">
                    Clear flag
                  </QuietButton>
                </>
              ) : (
                <QuietButton
                  onClick={openFlagEditor}
                  icon={Bookmark}
                  disabled={flagBusy}
                  data-testid="review-flag-btn"
                >
                  Flag for review
                </QuietButton>
              )}
            </div>
          </div>

          {flagOpen && (
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "#ddd6fe" }}>
              <Field label="Reason (optional)" hint="Why does this item need a review? Leave blank to just mark it.">
                <div data-testid="review-reason-input">
                  <Textarea
                    value={flagReason}
                    onChange={setFlagReason}
                    rows={2}
                    placeholder="e.g. rate looks low vs recent jobs; check plant allowance"
                  />
                </div>
              </Field>
              <div className="flex items-center gap-2 mt-2">
                <PrimaryButton
                  onClick={() => submitFlag(doc.needs_review)}
                  disabled={flagBusy}
                  icon={flagBusy ? Loader2 : Bookmark}
                  data-testid="review-submit-btn"
                >
                  {doc.needs_review ? "Update reason" : "Flag for review"}
                </PrimaryButton>
                <QuietButton onClick={() => setFlagOpen(false)} disabled={flagBusy} data-testid="review-cancel-btn">
                  Cancel
                </QuietButton>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Block Enter on the form so it never auto-saves */}
      <form onKeyDown={blockEnterSubmit} onSubmit={(e) => e.preventDefault()}>

        {/* Details card */}
        <Card className="p-7 mb-5" allowOverflow>
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {/* Row 1: SKU + Item type */}
            <Field label="SKU" hint="Auto-generated on save (LIB-XXXX). Immutable.">
              <Input
                value={form.sku || (editing ? "—" : "Will be assigned on save")}
                onChange={() => {}}
                mono
                readOnly
                data-testid="sku-readonly"
              />
            </Field>
            <Field label="Item type" required>
              <ToggleGroup
                value={form.item_type}
                onChange={set("item_type")}
                options={[
                  { value: "priced",          label: "Priced" },
                  { value: "provisional_sum", label: "Provisional sum" },
                  { value: "prelim",          label: "Prelim" }
                ]}
              />
            </Field>

            {/* Row 2: Description (full width) */}
            <div className="col-span-2">
              <Field label="Description" required>
                <Input
                  value={form.description}
                  onChange={set("description")}
                  placeholder="e.g. Supply & install C24 floor joists, 47×220mm at 400mm c/c"
                  data-testid="description-input"
                />
              </Field>
            </div>

            {/* Row 3: Unit + 3 rate fields */}
            <div className="col-span-2 grid gap-4" style={{ gridTemplateColumns: "180px 1fr 1fr 1fr" }}>
              <Field label="Unit (recipe yield)" required>
                <Select
                  value={form.unit}
                  onChange={set("unit")}
                  options={UNIT_OPTIONS}
                  placeholder="Pick…"
                  data-testid="unit-select"
                />
              </Field>
              <Field label="Labour rate" hint="Auto from labour recipe">
                <NumberInput
                  value={totals.recipe_labour_total}
                  onChange={() => {}}
                  readOnly
                  showZero
                  prefix="£"
                  suffix={form.unit ? `/ ${form.unit}` : ""}
                  data-testid="labour-rate-display"
                />
              </Field>
              <Field label="Material rate" hint="Auto from materials recipe">
                <NumberInput
                  value={totals.recipe_materials_total}
                  onChange={() => {}}
                  readOnly
                  showZero
                  prefix="£"
                  suffix={form.unit ? `/ ${form.unit}` : ""}
                  data-testid="material-rate-display"
                />
              </Field>
              <Field label="Plant rate" hint="Auto from plant recipe">
                <NumberInput
                  value={totals.recipe_plant_total}
                  onChange={() => {}}
                  readOnly
                  showZero
                  prefix="£"
                  suffix={form.unit ? `/ ${form.unit}` : ""}
                  data-testid="plant-rate-display"
                />
              </Field>
            </div>

            {/* Row 4: Trade (dropdown) */}
            <Field
              label={
                <span className="inline-flex items-center gap-2">
                  <span>Trade</span>
                  {form.trade_manual_override ? (
                    <span
                      data-testid="trade-mode-badge"
                      className="inline-flex items-center gap-1 rounded"
                      style={{
                        background: "#FEF3E2", color: "#7A4C0A",
                        border: "1px solid #F2DFB8",
                        padding: "1px 6px", fontSize: 9.5, fontWeight: 600,
                        letterSpacing: "0.04em", textTransform: "uppercase"
                      }}
                    >
                      Manual
                    </span>
                  ) : (
                    <span
                      data-testid="trade-mode-badge"
                      className="inline-flex items-center gap-1 rounded"
                      style={{
                        background: "#E8EFE9", color: "#2F4F3E",
                        border: "1px solid #D6E2D8",
                        padding: "1px 6px", fontSize: 9.5, fontWeight: 600,
                        letterSpacing: "0.04em", textTransform: "uppercase"
                      }}
                    >
                      Auto
                    </span>
                  )}
                  {form.trade_manual_override && (
                    <button
                      type="button"
                      onClick={resetTradeToAuto}
                      data-testid="trade-reset-to-auto"
                      className="text-accent hover:text-accent-deep underline-offset-2 hover:underline"
                      style={{ fontSize: 11, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}
                    >
                      Reset to auto
                    </button>
                  )}
                </span>
              }
              hint={
                form.trade_manual_override
                  ? "Manual selection — won't change when the recipe is edited."
                  : "Auto: derived from the trade with the most hours in the labour recipe — skip this field. Recalculates whenever the labour recipe changes."
              }
            >
              {form.trade_manual_override || tradeOverrideOpen ? (
              <Select
                value={tradeValue}
                onChange={(id) => {
                  const lr = labourRates.find((x) => x.id === id);
                  if (!lr) return;
                  setTradeOverrideOpen(false);
                  setForm((s) => ({
                    ...s,
                    trade_manual_override: true,
                    trade_name: lr.trade_name,
                    trade_code: lr.trade_code || "",
                  }));
                }}
                options={tradeOptions}
                placeholder={tradeOptions.length ? "Pick a trade…" : "No labour rates yet"}
                data-testid="trade-select"
              />
              ) : (
              // LT-38 — Auto mode: no picker to fill in. The trade is derived
              // from the labour recipe; show what it derived and an explicit
              // override link so nobody stops here wondering what to choose.
              <div
                className="flex items-center justify-between gap-3 h-9 px-3 rounded-md border border-dashed border-line bg-surface-2/40"
                style={{ fontSize: 12.5 }}
                data-testid="trade-auto-readout"
              >
                <span className="text-ink-2 truncate">
                  {form.trade_name
                    ? <>Auto — <span className="text-ink font-medium">{form.trade_name}</span>{autoTradeHours != null ? <span className="text-ink-3"> ({autoTradeHours} h in the recipe)</span> : null}. Nothing to pick.</>
                    : "Auto — set from the labour recipe once a labour line is added. Nothing to pick."}
                </span>
                <button
                  type="button"
                  onClick={() => setTradeOverrideOpen(true)}
                  data-testid="trade-override-manually"
                  className="text-accent hover:text-accent-deep hover:underline flex-shrink-0"
                  style={{ fontSize: 11.5, fontWeight: 500 }}
                >
                  Override manually →
                </button>
              </div>
              )}
            </Field>
            <div />

            {/* Row 5: Labour mode (Section 4) — full width so the explanation
                is legible. Placed above the Phase/Category tag row because
                mode fundamentally changes how hours behave when the item
                enters a tender. */}
            <div className="col-span-2">
              <Field
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>Labour mode</span>
                    <span
                      data-testid="labour-mode-badge"
                      className="inline-flex items-center gap-1 rounded"
                      style={{
                        background: form.labour_mode === "estimated_each_use" ? "#FEF3E2" : "#E8EFE9",
                        color: form.labour_mode === "estimated_each_use" ? "#7A4C0A" : "#2F4F3E",
                        border: `1px solid ${form.labour_mode === "estimated_each_use" ? "#F2DFB8" : "#D6E2D8"}`,
                        padding: "1px 6px", fontSize: 9.5, fontWeight: 600,
                        letterSpacing: "0.04em", textTransform: "uppercase"
                      }}
                    >
                      {form.labour_mode === "estimated_each_use" ? "Est. each use" : "Rate-based"}
                    </span>
                  </span>
                }
                hint={
                  form.labour_mode === "estimated_each_use"
                    ? "Hours are a judgement call each time this is used. On insert into a tender, hours arrive empty and focused. A £0 line will block send until hours are entered."
                    : "Hours-per-unit is a stable rate (e.g. 0.35 hrs/m²). Reprices when labour rates change."
                }
              >
                <div className="flex gap-2" role="radiogroup" aria-label="Labour mode">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.labour_mode !== "estimated_each_use"}
                    data-testid="labour-mode-rate-based"
                    onClick={() => set("labour_mode")("rate_based")}
                    className={cls(
                      "flex-1 rounded-md px-3 py-2 text-left border transition-colors",
                      form.labour_mode !== "estimated_each_use"
                        ? "border-accent bg-accent-soft text-ink"
                        : "border-line bg-surface text-ink-2 hover:bg-surface-2"
                    )}
                    style={{ fontSize: 12.5 }}
                  >
                    <div style={{ fontWeight: 600 }}>Rate-based</div>
                    <div className="text-ink-3" style={{ fontSize: 11 }}>
                      Priced per unit. Hours-per-unit × qty.
                    </div>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.labour_mode === "estimated_each_use"}
                    data-testid="labour-mode-estimated-each-use"
                    onClick={() => set("labour_mode")("estimated_each_use")}
                    className={cls(
                      "flex-1 rounded-md px-3 py-2 text-left border transition-colors",
                      form.labour_mode === "estimated_each_use"
                        ? "border-warning bg-warning-soft text-ink"
                        : "border-line bg-surface text-ink-2 hover:bg-surface-2"
                    )}
                    style={{ fontSize: 12.5 }}
                  >
                    <div style={{ fontWeight: 600 }}>Estimated each use</div>
                    <div className="text-ink-3" style={{ fontSize: 11 }}>
                      Ad-hoc labour hours. Enter per tender.
                    </div>
                  </button>
                </div>
              </Field>
            </div>

            {/* Row 6: Phase / Category tags (full width, allowCustom) */}
            <div className="col-span-2">
              <Field
                label="Phase / Category tags"
                hint="Multi-select — surface this item in the relevant phases and categories. Type to create a custom tag."
              >
                <MultiSelect
                  value={form.phase_tags}
                  onChange={set("phase_tags")}
                  options={phases}
                  tone="accent"
                  placeholder="Tag a phase or category"
                  allowCustom
                />
              </Field>
            </div>
          </div>
        </Card>

        {/* RECIPE BUILDER */}
        <div className="flex items-center gap-3 mb-3">
          <h2 className="font-display text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Recipe</h2>
          <span className="text-ink-3" style={{ fontSize: 12 }}>
            Per-unit breakdown of labour, materials and plant.
          </span>
        </div>

        {/* LT-32 G2 — pre-fill suggestion banner. Shown only when the draft's
            labour hours / plant rate were seeded from imported reference
            figures. Editable starting points; nothing is saved until Save. */}
        {prefillNote && (
          <div
            className="mb-4 flex items-start gap-2 px-4 py-3 rounded-md border border-accent bg-accent-soft"
            data-testid="library-prefill-banner"
          >
            <Sparkles size={14} className="text-accent mt-0.5 flex-shrink-0" />
            <div className="text-accent-deep" style={{ fontSize: 12.5 }}>
              <strong>Suggested from import.</strong>{" "}
              {prefillNote.hours != null && (
                <>Labour hours pre-filled to <strong>{prefillNote.hours}</strong> h/unit</>
              )}
              {prefillNote.hours != null && prefillNote.plant != null && " and "}
              {prefillNote.plant != null && (
                <>a plant line pre-filled to <strong>£{prefillNote.plant}</strong>/unit</>
              )}
              {" "}from the CSV reference figures. Pick the trade and rate, adjust as needed —
              these are editable suggestions and <strong>nothing is saved until you click Save</strong>.
              The material line is not pre-filled.
            </div>
          </div>
        )}

        <div className="grid gap-5 mb-5" style={{ gridTemplateColumns: "1fr 320px" }}>
          <div>
            <LabourSection
              entries={form.recipe_labour}
              onChange={set("recipe_labour")}
              labourRates={labourRates}
            />
            <MaterialsSection
              entries={form.recipe_materials}
              onChange={set("recipe_materials")}
              materials={materials}
              flat={flat}
              onFlatChange={setFlat}
              refRate={doc?.ref_material_rate ?? null}
              initialBuildOut={buildOutRequested && !!form.flat_material_enabled}
            />
            <SimpleCostSection
              title="Plant" icon={Wrench} accent="#F1ECF6"
              entries={form.recipe_plant} onChange={set("recipe_plant")}
              placeholder="No plant entries yet (e.g. skip hire share, scaffold contribution)."
              addLabel="Add plant cost"
            />
            {/* Subby section retained for legacy items. Hidden behind a toggle to keep the page focused. */}
            {(form.recipe_subby?.length > 0) && (
              <SimpleCostSection
                title="Subby / PS" icon={Sparkles} accent="#EBE9F4"
                entries={form.recipe_subby} onChange={set("recipe_subby")}
                placeholder="No subby costs."
                addLabel="Add subby cost"
              />
            )}
          </div>
          <div className="sticky top-[110px] self-start">
            <RecipeSummary totals={totals} unit={form.unit} />
            <div className="mt-3 px-3 py-2.5 rounded-md bg-accent-soft text-accent" style={{ fontSize: 11.5 }}>
              <Sparkles size={11} className="inline mr-1" />
              When this item is added to a tender, a snapshot of these per-unit values is captured. Tender estimate cells auto-flag overrides when they deviate from the snapshot.
            </div>
            {/* LT-27 — Imported reference panel (draft or promoted item that
                carries imported reference data). Read-only, display-only. */}
            {editing && doc && doc.ref_rate_total != null && (
              <ReferencePanel doc={doc} builtUpRate={totals.has_recipe ? totals.current_rate : null} />
            )}
            {/* LT-28 — Price history ledger for this library item. */}
            {editing && doc && doc.status !== "draft" && (
              <div className="mt-3">
                <PriceHistoryPanel
                  entityType="library_item"
                  entityId={doc.id}
                  fieldLabel="rate"
                />
              </div>
            )}
          </div>
        </div>

        {/* Notes + bottom actions */}
        <Card className="p-7">
          <Field
            label="Default note"
            hint="Optional. Copied onto a tender line when this item is added, then editable per tender. Use for wording that recurs — most items won't need one."
          >
            <Textarea
              value={form.default_note}
              onChange={set("default_note")}
              placeholder="e.g. Rate assumes prime cost sum for ironmongery"
              data-testid="library-default-note"
            />
          </Field>
          <div className="mt-5" />
          <Field label="Notes (internal)">
            <Textarea value={form.notes} onChange={set("notes")} />
          </Field>

          <div className="mt-7 flex items-center justify-between">
            {editing ? (
              <button
                type="button"
                onClick={archive}
                className="text-note-red hover:underline inline-flex items-center gap-1.5"
                style={{ fontSize: 12 }}
                data-testid="archive-item-bottom"
              >
                <Trash2 size={12} /> Archive item
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <QuietButton onClick={() => navigate("/library")} icon={ChevronLeft}>Cancel</QuietButton>
              <PrimaryButton
                onClick={triggerSave}
                disabled={saving}
                icon={saving ? Loader2 : Save}
                data-testid="save-item-bottom"
              >
                {editing ? "Save changes" : "Create item"}
              </PrimaryButton>
            </div>
          </div>
        </Card>
      </form>

      <ValidationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        checks={checks}
        onConfirm={performSave}
        canPromote={editing && doc?.status === "draft" && totals.has_recipe && totals.current_rate > 0}
      />
    </div>
  );
}

/* =========================================================================
 * LT-27 additions — status badge, reference panel, promote-with-recipe CTA
 * ========================================================================= */

function StatusChip({ status, isBuiltUp }) {
  const isDraft = status === "draft";
  return (
    <div className="inline-flex items-center gap-2">
      <span
        className="inline-flex items-center rounded font-mono uppercase"
        style={{
          padding: "2px 8px",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          background: isDraft ? "#FEF3E2" : "#E8EFE9",
          color: isDraft ? "#7A3E06" : "#23402F",
          border: `1px solid ${isDraft ? "#F0D9A8" : "#D6E2D8"}`,
        }}
        data-testid="library-edit-status-chip"
      >
        {status || "live"}
      </span>
      {!isBuiltUp && (
        <span
          className="inline-flex items-center gap-1 rounded font-mono uppercase"
          style={{
            padding: "2px 8px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.06em",
            background: "#FEF3E2",
            color: "#7A3E06",
            border: "1px solid #F0D9A8",
          }}
          data-testid="library-edit-not-built-up"
          title="No recipe or manually-confirmed flat rate"
        >
          <AlertTriangle size={10} /> Not built up
        </span>
      )}
    </div>
  );
}

function ReferencePanel({ doc, builtUpRate }) {
  // Format an ISO date as DD/MM/YYYY. Falls back to raw string on parse failure.
  const displayDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const yyyy = d.getUTCFullYear();
      if (!yyyy || Number.isNaN(yyyy)) return iso;
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return iso;
    }
  };

  const ref = doc.ref_rate_total;
  let variance = null;
  let varianceAmber = false;
  if (builtUpRate != null && ref != null && ref > 0) {
    const pct = ((builtUpRate - ref) / ref) * 100;
    variance = pct;
    varianceAmber = Math.abs(pct) > 15;
  }

  return (
    <div
      className="mt-4 rounded-md border overflow-hidden"
      style={{ borderColor: "#B6C9DA" }}
      data-testid="library-reference-panel"
    >
      <div
        className="px-3 py-2 border-b flex items-center gap-2"
        style={{ background: "#E0EAF1", borderColor: "#B6C9DA" }}
      >
        <FileText size={11} className="text-info" />
        <span
          className="col-head"
          style={{ fontSize: 9.5, color: "#173E5C", fontWeight: 700, letterSpacing: "0.06em" }}
        >
          Imported Reference · Display Only
        </span>
      </div>
      <div className="px-3 py-3 space-y-1.5 bg-surface" style={{ fontSize: 11.5 }}>
        <RefRow label="Labour" value={doc.ref_labour_rate != null ? gbp(doc.ref_labour_rate) : "—"} />
        <RefRow label="Material" value={doc.ref_material_rate != null ? gbp(doc.ref_material_rate) : "—"} />
        <RefRow label="Combined" value={doc.ref_rate_total != null ? gbp(doc.ref_rate_total) : "—"} strong />
        {doc.ref_rate_range && <RefRow label="Range" value={doc.ref_rate_range} />}
        <RefRow label="As at" value={displayDate(doc.ref_rate_as_at) || "—"} />
        {/* LT-32 — imported reference figures. */}
        {doc.ref_hours_median != null && <RefRow label="Hours (median)" value={`${doc.ref_hours_median} h/unit`} />}
        {doc.ref_plant_rate != null && <RefRow label="Plant (median)" value={gbp(doc.ref_plant_rate)} />}
        {doc.ref_year_range && <RefRow label="Year range" value={doc.ref_year_range} />}
        {doc.rate_confidence && <RefRow label="Confidence" value={String(doc.rate_confidence).replace(/_/g, " ")} />}
        <RefRow label="Frequency" value={`${doc.source_frequency || 0} tender${(doc.source_frequency || 0) === 1 ? "" : "s"}`} />
        {doc.source_refs && (
          <div className="pt-2 border-t border-line-soft">
            <div className="col-head text-ink-4" style={{ fontSize: 9.5 }}>Source refs</div>
            <div className="text-ink-3 font-mono mt-0.5" style={{ fontSize: 11 }}>{doc.source_refs}</div>
          </div>
        )}
        {variance != null && (
          <div
            className="mt-2 pt-2 border-t border-line-soft flex items-center justify-between"
            data-testid="library-reference-variance"
          >
            <span className="text-ink-3" style={{ fontSize: 11 }}>Built-up vs ref</span>
            <span
              className="font-mono tnum"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: varianceAmber ? "#B45309" : "#23402F",
              }}
            >
              {gbp(builtUpRate)} · {variance >= 0 ? "+" : ""}{variance.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function RefRow({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-3" style={{ fontSize: 11 }}>{label}</span>
      <span
        className={"font-mono tnum " + (strong ? "text-ink" : "text-ink-2")}
        style={{ fontSize: strong ? 12.5 : 11.5, fontWeight: strong ? 600 : 500 }}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Header CTA for draft items — routes through the /promote endpoint in
 * "recipe" mode when the current recipe produces a rate > 0. Uses the LIVE
 * form totals so the button is enabled/disabled based on what the user
 * currently sees, not the last-saved state.
 *
 * Guards:
 *   • Requires a saved recipe on the server (backend recomputes and
 *     enforces rate > 0). This button additionally short-circuits when
 *     `totals.has_recipe` is false in the current form.
 *   • If the user has unsaved changes, we prompt them to save first — the
 *     backend enforces its own rate check against the persisted state.
 */
function PromoteWithRecipeButton({ doc, totals, onDone }) {
  const [busy, setBusy] = useState(false);
  const canPromoteWithRecipe = totals.has_recipe && totals.current_rate > 0;

  const promote = async () => {
    if (!canPromoteWithRecipe) {
      toast.error(
        "Build a recipe with a positive rate first, or use the Library page to promote as a flat rate."
      );
      return;
    }
    if (!window.confirm(
      "Promote this item to Live using the current built-up recipe?\n\nSave any unsaved changes first — promotion reads the saved recipe."
    )) return;
    setBusy(true);
    try {
      await api.post(`/library/${doc.id}/promote`, { mode: "recipe" });
      toast.success("Promoted to Live with recipe");
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuietButton
      onClick={promote}
      icon={busy ? Loader2 : ArrowUpFromLine}
      disabled={busy}
      data-testid="promote-with-recipe-top"
    >
      Promote with recipe
    </QuietButton>
  );
}

